
<template>
  <div>
    <h4 v-if="isDispatched">
      Order is already dispatched.
    </h4>
    <div v-else>
    <div style="gap:2px;">
      <p style="font-size:13px;"><b>Select Recipient</b></p>
      <p style="font-size:14px;">
        When selecting several recipients, set them in priority order. Each offer is sent to the next recipient after the previous one expires.
      </p>
      <p style="font-size:14px;">
        Select from <span class="green-text" style="font-size:14px;">●</span> <b>existing HCL users</b> or to invite <span class="yellow-text" style="font-size:14px;">●</span> <b>new recipients</b>.
      </p>
    </div>
    
    <form >
      <div class='d-flex flex-column' style="gap:16px;">
        <div v-for="(entry, i) in entries" :key="`${formKey}-${i}`" class="d-flex flex-column" style="gap:8px;">
          <div class="d-flex align-items-start" style="gap:32px;">

            <div class="d-flex flex-column" style="gap:4px; width:300px;">
              <label style="font-size:13px; font-weight:600;">Recipient #{{ i + 1 }}</label>
              <carrier-picker :show-hcl-status="true" @input="carrier => selectCarrier(i, carrier)" />
            </div>

            <template v-if="entry.selectedRecipient !== null">
              <div class='d-flex flex-column justify-content-center' style="flex-wrap:wrap; gap:10px;">
                <div style="font-size:13px; font-weight:600;">Offer Expiration</div>
                <div class='d-flex' style="flex-wrap:wrap; gap:4px;">
                  <rounded-button
                    v-for="opt in expiryOptions"
                    :key="opt.value"
                    type="form"
                    :active="entry.selectedExpiry === opt.value"
                    @click.native="entry.selectedExpiry = opt.value"
                  >{{ opt.text }}</rounded-button>
                </div>
              </div>
              <div class='d-flex flex-column justify-content-center' style="flex-wrap:wrap; gap:10px;">
                <div style="font-size:13px; font-weight:600;">Price</div>
                <div class='d-flex' style="flex-wrap:wrap; gap:4px;">
                  <template v-if="order.carrierPay > 0">
                    <rounded-button
                      v-for="opt in priceOptions"
                      :key="opt.value"
                      type="form"
                      :active="entry.selectedPrice === opt.value"
                      @click.native="entry.selectedPrice = opt.value; entry.customPrice = ''"
                      style="margin-right:4px;"
                    >{{ opt.text }}</rounded-button>
                  </template>
                  <div class="custom-price-wrapper" :class="{ 'custom-price-active': entry.selectedPrice === null && entry.customPrice !== '' }">
                    <span class="custom-price-prefix">$</span>
                    <input
                      v-model.number="entry.customPrice"
                      type="number"
                      placeholder="Custom price"
                      class="custom-price-input"
                      @input="entry.selectedPrice = null"
                    />
                  </div>
                </div>
              </div>
              <button v-if="!entry.showNote" class="text-button" style="align-self:center; margin-top:18px;" @click.prevent="entry.showNote = true"> + Add Note </button>
            </template>
          </div>
          <div v-if="entry.showNote && entry.selectedRecipient !== null" class="d-flex flex-column" style="padding-left:332px; gap:6px; padding-bottom: 16px;">
            <div style="font-size:13px; font-weight:600;">Notes</div>
            <b-input v-model="entry.note" type="text" placeholder="Add notes" style="width: 100%;"/>
          </div>
        </div>
        <button class="text-button" :disabled="!canAddMore" @click.prevent="addEntry" style="align-self:flex-start;"> + Add More Recipient </button>
        <b-btn size="md" variant="primary" style="align-self:flex-start;" @click.prevent="sendOffer">Send Offer</b-btn>
      </div>
    </form>
    </div>
  </div>
</template>

<script>
import RoundedButton from '@/components/shared/RoundedButton.vue';
import CarrierPicker from '@/components/shared/CarrierPicker.vue';
import { ApiClient } from '@/api/ApiClient';

function newEntry(price) {
  return {
    selectedRecipient: null,
    selectedExpiry: null,
    selectedPrice: price,
    customPrice: '',
    showNote: false,
    note: '',
  };
}

export default {
  components: { RoundedButton, CarrierPicker },
  props: {
    order: { type: Object, required: true },
    isDispatched: { type: Boolean, default: false },
  },
  data() {
    return {
      entries: Array.from({ length: 3 }, () => newEntry(parseInt(this.order.carrierPay))),
      formKey: 0,
      expiryOptions: [
        { text: 'No Expiry', value: null },
        { text: '30m', value: 30 },
        { text: '3h', value: 180 },
        { text: '6h', value: 360 },
        { text: '12h', value: 720 },
        { text: '24h', value: 1440 },
        { text: '2 days', value: 2880 },
      ],
    };
  },
  computed: {
    priceOptions() {
      const base = parseInt(this.order.carrierPay);
      return [
        { text: `$${base}`, value: base },
        { text: `$${base + 50}`, value: base + 50 },
        { text: `$${base + 100}`, value: base + 100 },
      ];
    },
    canAddMore() {
      return this.entries.every(e => e.selectedRecipient !== null);
    },
  },
  methods: {
    selectCarrier(i, carrier) {
      this.entries[i].selectedRecipient = carrier ? carrier.id : null;
    },
    addEntry() {
      this.entries.push(newEntry(parseInt(this.order.carrierPay)));
    },
    async sendOffer() {
      const recipients = this.entries
        .filter(e => e.selectedRecipient !== null)
        .map(e => ({
          carrier_id: e.selectedRecipient,
          expires_minutes: e.selectedExpiry !== null ? e.selectedExpiry : null,
          price: e.selectedPrice !== null ? parseInt(e.selectedPrice) : parseInt(e.customPrice),
          note: e.note || undefined,
        }));
        console.log('Sending offer with recipients:', recipients);
      const response = await ApiClient.leads.loadboardOffers.post({ leadId: this.order.id, body: { recipients } });
      if (response.status === 200 || response.status === 201) {
        this.entries = Array.from({ length: 3 }, () => newEntry(parseInt(this.order.carrierPay)));
        this.formKey++;
        this.$emit('refresh');
      }
    },
  },
};
</script>

<style>
.custom-price-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.custom-price-prefix {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: #4E5155;
  pointer-events: none;
}
.dark-style .custom-price-prefix {
  color: #B3B4B4;
}
.custom-price-input {
  border-radius: 100px;
  padding: 0 8px 0 20px;
  height: 30px;
  border: 1px solid #AEB3B8;
  color: #4E5155;
  background-color: transparent;
  width: 130px;
  outline: none;
}
.dark-style .custom-price-input {
  color: #B3B4B4;
}
.custom-price-active .custom-price-input {
  color: white;
  background-color: #3E4144;
  border-color: #52565B;
}
.custom-price-active .custom-price-prefix {
  color: white;
}
.dark-style .custom-price-active .custom-price-input {
  color: #26282D;
  background-color: #C1C1C1;
  border-color: #AEB3B8;
}
.dark-style .custom-price-active .custom-price-prefix {
  color: #26282D;
}
.text-button {
  background: none;
  border: none;
  padding: 0;
  color: #0047D6;
  cursor: pointer;
  font-size: 13px;
  font-weight: 590;
}
.text-button:disabled {
  color: #aaa;
  cursor: not-allowed;
}

</style>
